import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Download, EllipsisVertical, FileText, FolderOpen, Image as ImageIcon, Presentation, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { getPersonalLibraryPage } from "@/api/api";
import ErrorDetails from "@/components/Message/ErrorDetails";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import { AppStateContext } from "@/state/AppStateContext";
import { newChat, setTitle, toggleHistoryPanel, useHandleTransitionEnd } from "@/utils/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { downloadUrl, filterLibraryItems, getLibraryItemLabel, getPresentationFilename, LibraryFilter, PersonalLibraryItem } from "./libraryUtils";

const filters: Array<{ value: LibraryFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "files", label: "Files" },
  { value: "uploaded", label: "Uploaded images" },
  { value: "generated", label: "Generated images" },
  { value: "group", label: "Group Chats" },
];

const formatDate = (date?: string | null) => {
  if (!date) return "";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString();
};

const Library = () => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  useHandleTransitionEnd();
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const previousSearchRef = useRef("");
  const [searchText, setSearchText] = useState<string>('');
  const [items, setItems] = useState<PersonalLibraryItem[]>([]);
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loadPage = async (nextCursor: string | null = null, search = searchText) => {
    if (loadingRef.current || (nextCursor !== null && !hasMore)) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const page = await getPersonalLibraryPage(nextCursor, 24, search);
      setItems((current) => nextCursor === null ? page.items : [...current, ...page.items]);
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    setTitle(appStateContext, 'Library');
    newChat(appStateContext);
    void loadPage();
  }, []);

  useEffect(() => {
    if (searchText === previousSearchRef.current) return;
    previousSearchRef.current = searchText;
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setLoading(true);
    const timeout = window.setTimeout(() => void loadPage(null, searchText), 250);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingRef.current && cursor) void loadPage(cursor);
    }, { rootMargin: "240px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [cursor, hasMore]);

  const visibleItems = useMemo(() => filterLibraryItems(items, filter), [items, filter]);
  const openItem = (item: PersonalLibraryItem) => {
    if (item.conversationId) navigate(item.isGroup ? `/gc/${item.conversationId}` : `/c/${item.conversationId}`);
  };

  const downloadItem = (item: PersonalLibraryItem) => {
    if (!item.url) return;
    const filename = item.kind === "presentation"
      ? getPresentationFilename(item.conversationTitle, item.filename)
      : item.filename || "download";
    void downloadUrl(item.url, filename);
  };

  const canDownload = (item: PersonalLibraryItem) => Boolean(item.url);

  return (
    <div className={`content flex flex-col h-full ${appStateContext?.state.sidepanelOpen ? "shifted-left" : ""}`}>
      {!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning &&
        <div className="flex justify-between items-center mx-2">
          <button type="button" className="btn btn-ghost px-2" onClick={handleToggleHistoryPanel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118ZM11.5 5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V10.4C20.5 9.26339 20.4992 8.47108 20.4488 7.85424C20.3994 7.24907 20.3072 6.90138 20.173 6.63803C19.8854 6.07354 19.4265 5.6146 18.862 5.32698C18.5986 5.19279 18.2509 5.10062 17.6458 5.05118C17.0289 5.00078 16.2366 5 15.1 5H11.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z" fill="currentColor"></path></svg>
          </button>
        </div>
      }

      <div className="flex flex-grow justify-center h-[90vh]">
        <div className="w-full mx-auto">
          <div className="main-div flex flex-col flex-grow items-center justify-center !h-full transitioned">
            <div className="child-div flex flex-col w-full gg">
              <div className="child-div-parent flex flex-col flex-grow w-full items-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent pb-2">
                <div className="flex flex-col w-full text-start px-6">
                  <div className={`flex items-start justify-between gap-4 ${((!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning) ? '' : 'mt-4')}`}>
                    <div className="flex gap-4">
                      <div>
                        <h1 className="dark:text-white text-3xl font-bold">Library</h1>
                        {!window.matchMedia('(max-width: 700px)').matches ?
                          <h4 className="dark:text-gray-400 text-sm mt-4">Files and images you have added or generated in conversations.</h4>
                        : null}
                      </div>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="w-full mt-6">
                    <label className="input bg-neutral border-accent border-opacity-50 focus-within:border-accent focus-within:border-opacity-50 focus-within:outline-none flex items-center gap-2">
                      <Search size={16} className="opacity-70 z-0" />
                      <input type="text" className="grow" placeholder="Search Library..." autoComplete="off" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2 border-b border-base-300 my-4" role="tablist" aria-label="Library filters">
                    {filters.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={filter === option.value}
                        className={`px-3 py-2 text-sm border-b-2 transition-colors ${filter === option.value ? "border-primary text-primary font-semibold" : "border-transparent opacity-70 hover:opacity-100"}`}
                        onClick={() => setFilter(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {error !== null && (
                    <div className="mb-4 rounded-xl border border-error/30 p-4 text-error" role="alert">
                      <p className="text-sm">{getErrorSummary(error)}</p>
                      <ErrorDetails details={getErrorDetails(error)} error={error} />
                      <button type="button" className="btn btn-sm btn-ghost mt-2" onClick={() => void loadPage(cursor)}>
                        Try again
                      </button>
                    </div>
                  )}

                  {loading && items.length === 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" aria-label="Loading library">
                      {Array.from({ length: 12 }, (_, index) => index).map((x, idx) =>
                        <div key={idx} className="flex flex-col items-stretch bg-base-100 border border-black/5 dark:border-white/5 rounded-3xl">
                          <div className="skeleton h-40 w-full rounded-b-none"></div>
                          <div className="flex flex-col flex-grow min-w-0 py-2 px-5">
                            <div className="skeleton h-4 w-full mb-1"></div>
                            <div className="skeleton h-3 w-2/3 mb-1"></div>
                            <div className="skeleton h-3 w-1/3"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : visibleItems.length === 0 && !error ? (
                    <div className="py-20 text-center opacity-70">Nothing here yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {visibleItems.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-xl border border-base-300 bg-base-100 overflow-hidden cursor-pointer hover:border-primary hover:opacity-75 transition-colors"
                          onClick={() => openItem(item)}
                          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openItem(item); } }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Open ${getLibraryItemLabel(item)}`}
                        >
                          {item.kind === "presentation" ? (
                            <div className="h-40 flex flex-col items-center justify-center gap-3 bg-primary/5 px-3 text-center">
                              <Presentation size={40} className="text-primary" />
                              <span className="text-sm font-medium break-all line-clamp-2">{getLibraryItemLabel(item)}</span>
                            </div>
                          ) : item.kind === "file" ? (
                            <div className="h-40 flex flex-col items-center justify-center gap-3 bg-base-200 px-3 text-center">
                              <FileText size={40} className="text-primary" />
                              <span className="text-sm font-medium break-all line-clamp-2">{getLibraryItemLabel(item)}</span>
                            </div>
                          ) : item.url ? (
                            <img src={item.url} alt={getLibraryItemLabel(item)} className="w-full h-40 object-cover" loading="lazy" />
                          ) : (
                            <div className="h-40 flex items-center justify-center bg-base-200"><ImageIcon size={40} className="opacity-50" /></div>
                          )}
                          <div className="flex items-center gap-1 ps-3 pe-0.5 py-2 min-w-0">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm truncate">{getLibraryItemLabel(item)}</p>
                              <p className="text-xs opacity-60 truncate">{item.conversationTitle}</p>
                              <p className="text-xs opacity-60 truncate">{formatDate(item.createdAt)}</p>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-square border-none bg-transparent hover:bg-secondary shrink-0"
                                  aria-label={`Actions for ${getLibraryItemLabel(item)}`}
                                  onClick={(event) => event.stopPropagation()}
                                  onKeyDown={(event) => event.stopPropagation()}
                                >
                                  <EllipsisVertical size={16} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-secondary border-transparent rounded-xl shadow-short p-1.5">
                                {item.conversationId ? (
                                  <DropdownMenuItem className="rounded-lg cursor-pointer text-xs gap-2" onClick={(event) => { event.stopPropagation(); openItem(item); }}>
                                    <FolderOpen size={12} /> Open conversation
                                  </DropdownMenuItem>
                                ) : null}
                                {canDownload(item) ? (
                                  <DropdownMenuItem className="rounded-lg cursor-pointer text-xs gap-2" onClick={(event) => { event.stopPropagation(); downloadItem(item); }}>
                                    <Download size={12} /> Download
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div ref={sentinelRef} className="h-10 flex items-center justify-center" aria-live="polite">
                    {loading && items.length > 0 && <div className="loading-icon w-6 h-6"></div>}
                    {!loading && hasMore && cursor && <button type="button" className="btn btn-sm btn-ghost" onClick={() => void loadPage(cursor)}>Load more</button>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Library;
